import { differenceInMinutes } from 'date-fns';
import sample from 'lodash/sample';
import { ITask as IUserTask } from '@bridebook/models/source/models/Users/Tasks.types';
import { ITask as IWeddingTask } from '@bridebook/models/source/models/Weddings/Tasks.types';
import { CountryCodes, Market } from '@bridebook/toolbox/src/gazetteer';
import { TodayTaskStorage } from '@bridebook/web/lib/task/today-task-storage';
import { TTodayTaskStorageKey } from '@bridebook/web/lib/task/types';
import { IDeps } from '@bridebook/web/lib/types';
import { selectCurrentUser } from '@bridebook/web/lib/users/selectors';

export const SESSION_DURATION_IN_MINS = 60 * 8;

export const registeredTodayTasks: Record<TTaskDefinitionUI['id'], TTaskDefinitionUI> = {};

export const registerTodayTask = (todayTask: TTaskDefinitionUI) => {
  registeredTodayTasks[todayTask.id] = todayTask;
};

export interface ITaskFlowContext {
  getState: IDeps['getState'];
  market: Market;
}

export type TTaskDefinitionUI = {
  name: string;
  type: 'interaction' | 'quiz' | 'article' | 'randomArticle' | 'featureCard' | 'generic';
  isTaskCompleted: (taskFlowContext: ITaskFlowContext) => boolean;
  isAvailable?: (taskFlowContext: ITaskFlowContext) => boolean;
  storage: TTodayTaskStorageKey;
  articleSlug?: string;
  markets?: CountryCodes[];
  /** When completed, also mark this mirror task as completed (A/B dual-write). */
  mirrorTask?: Pick<TTaskDefinitionUI, 'id' | 'storage' | 'name'>;
} & (
  | {
      id: IWeddingTask['id'];
      storage: 'wedding';
    }
  | {
      id: IUserTask['id'];
      storage: 'user';
    }
  | {
      id: string;
      storage: 'none';
    }
);

type SessionId = number;

export type TSessionDefinition = {
  id: SessionId;
  tasks: TTaskDefinitionUI[];
  taskSelectionOrder: 'sequence' | 'randomized';
  isSessionCompleted: (params: { sessionStarted?: number; sessionSignaled: boolean }) => boolean;
  fallbackTask?: TTaskDefinitionUI;
};

export type TTaskFlowDefinition = {
  id: string;
  sessions: TSessionDefinition[];
};

export type TTaskFlowInstance = {
  currentSession: {
    id: TSessionDefinition['id'];
    started: number;
    // session is signaled when we got event from a task (so basically when a task is interacted) When we get event, engine checks if we can go to next step - next task or session
    signaled: boolean;
    notCompletedTasks: TTaskDefinitionUI['id'][];
    availableTasks: TTaskDefinitionUI['id'][];
  };
  currentTaskId: TTaskDefinitionUI['id'];
  taskFlowDefinitionId: string;
};

const isSessionCompleted: TSessionDefinition['isSessionCompleted'] = ({ sessionStarted }) => {
  const currentDate = Date.now();
  // todo add check if last activity > 5minutes?
  if (sessionStarted)
    return (
      differenceInMinutes(currentDate, sessionStarted) >= SESSION_DURATION_IN_MINS
      // for the first version we are not using the 'sessionSignaled' value
      // more info here: https://bridebook.atlassian.net/browse/LIVE-18018
      // && sessionSignaled
    );
  else return false;
};

export const createSession = (
  sessionDefinition: Omit<TSessionDefinition, 'isSessionCompleted' | 'taskSelectionOrder'>,
  taskSelectionOrder: TSessionDefinition['taskSelectionOrder'] = 'sequence',
) => ({
  ...sessionDefinition,
  taskSelectionOrder,
  isSessionCompleted,
});

export const getNotCompletedTasksInSession = (
  session: TSessionDefinition,
  context: ITaskFlowContext,
) => {
  const notCompletedTasks = session.tasks.filter((task) => !task.isTaskCompleted(context));
  if (notCompletedTasks.length === 0 && session.fallbackTask) {
    return [session.fallbackTask];
  }

  return notCompletedTasks;
};

export const getNotCompletedTaskIdsInSession = (
  session: TSessionDefinition,
  context: ITaskFlowContext,
) => getNotCompletedTasksInSession(session, context).map((task) => task.id);

export const getAvailableTasksInSession = (
  session: TSessionDefinition,
  context: ITaskFlowContext,
) => {
  const availableTasks = session.tasks.filter((task) => {
    if (!task.isAvailable)
      return true; //assume that if function is not implemented return that task
    else {
      return task.isAvailable(context);
    }
  });

  return availableTasks;
};

export const getAvailableTaskIdsInSession = (
  session: TSessionDefinition,
  context: ITaskFlowContext,
) => getAvailableTasksInSession(session, context).map((task) => task.id);

/**
 * Skips past sessions where every real task (excluding fallback) is already
 * completed. Handles cross-device sync and long-absence catch-up: when task
 * completion lives in Firestore but session state is local-only, a new device
 * would otherwise sit on a fallback card for `sessionsToComplete` days per
 * already-finished session.
 */
export const fastForwardCompletedSessions = (
  taskFlowDefinition: TTaskFlowDefinition,
  context: ITaskFlowContext,
  instance: TTaskFlowInstance,
): TTaskFlowInstance => {
  let current = instance;

  for (let hop = 0; hop < taskFlowDefinition.sessions.length; hop++) {
    const session = taskFlowDefinition.sessions.find((s) => s.id === current.currentSession.id);
    if (!session) break;

    // If any real task is still incomplete, this is the right session
    if (session.tasks.some((task) => !task.isTaskCompleted(context))) break;

    // Last session — nowhere to advance
    const idx = taskFlowDefinition.sessions.indexOf(session);
    if (idx >= taskFlowDefinition.sessions.length - 1) break;

    // Advance to next session
    const nextSession = taskFlowDefinition.sessions[idx + 1];
    const notCompletedTasks = getNotCompletedTasksInSession(nextSession, context);
    const nextTask =
      nextSession.taskSelectionOrder === 'randomized'
        ? sample(notCompletedTasks)
        : notCompletedTasks[0];
    if (!nextTask) break;

    current = {
      ...current,
      currentSession: {
        id: nextSession.id,
        started: Date.now(),
        signaled: false,
        notCompletedTasks: notCompletedTasks.map((t) => t.id),
        availableTasks: getAvailableTaskIdsInSession(nextSession, context),
      },
      currentTaskId: nextTask.id,
    };
  }

  return current;
};

export const getCurrentTaskAndIndex = (todayTaskFlowInstance: TTaskFlowInstance) => {
  const currentTaskId = todayTaskFlowInstance.currentTaskId;
  const foundIndex = todayTaskFlowInstance.currentSession.notCompletedTasks.findIndex(
    (notCompletedTask) => notCompletedTask === currentTaskId,
  );
  const currentTaskIndex = foundIndex >= 0 ? foundIndex : 0;

  return {
    currentTaskIndex,
    currentTaskId,
  };
};

export const getNextTaskFlowInstance = (
  taskFlowDefinition: TTaskFlowDefinition,
  context: ITaskFlowContext,
  taskFlowInstance: TTaskFlowInstance,
  signalSession: boolean,
) => {
  const taskFlowInstanceWithSignal = signalSession
    ? {
        ...taskFlowInstance,
        currentSession: {
          ...taskFlowInstance.currentSession,
          signaled: true,
        },
      }
    : taskFlowInstance;

  const nextSession = getNextSession(taskFlowDefinition, taskFlowInstanceWithSignal);
  const notCompletedTasks = getNotCompletedTaskIdsInSession(nextSession, context);
  const availableTasks = getAvailableTaskIdsInSession(nextSession, context);
  const nextTask = getTaskFromSession(nextSession, context);
  if (!nextTask) throw new Error('Something went wrong');

  const nextWorkFlowInstance: TTaskFlowInstance = {
    ...taskFlowInstance,
    currentSession:
      nextSession.id === taskFlowInstance.currentSession.id
        ? {
            ...taskFlowInstance.currentSession,
            signaled: signalSession ? true : taskFlowInstance.currentSession.signaled,
            notCompletedTasks,
            availableTasks,
          }
        : {
            id: nextSession.id,
            started: Date.now(),
            signaled: false,
            notCompletedTasks,
            availableTasks,
          },
    currentTaskId: nextTask.id,
  };

  return nextWorkFlowInstance;
};

export const migrateTaskFlowInstance = (
  taskFlowDefinition: TTaskFlowDefinition,
  context: ITaskFlowContext,
  taskFlowInstance: TTaskFlowInstance,
) => {
  const nextSession = getNextSession(taskFlowDefinition, taskFlowInstance);
  const notCompletedTasks = getNotCompletedTaskIdsInSession(nextSession, context);
  const availableTasks = getAvailableTaskIdsInSession(nextSession, context);

  const newDefinitionIncludesCurrentTaskId = !!taskFlowDefinition.sessions
    .find((session) => session.id === nextSession.id)
    ?.tasks.some((task) => task.id === taskFlowInstance.currentTaskId);

  const nextTask = getTaskFromSession(nextSession, context);
  if (!nextTask) throw new Error('Something went wrong');

  const currentTaskId =
    taskFlowInstance.currentSession.id === nextSession.id
      ? newDefinitionIncludesCurrentTaskId
        ? taskFlowInstance.currentTaskId
        : nextTask?.id
      : taskFlowDefinition.sessions[taskFlowDefinition.sessions.length - 1].tasks[0].id;

  const migratedTaskFlowInstance = {
    currentSession: {
      id: nextSession.id,
      started: Date.now(),
      signaled: false,
      notCompletedTasks,
      availableTasks,
    },
    currentTaskId,
    taskFlowDefinitionId: taskFlowDefinition.id,
  };

  return migratedTaskFlowInstance;
};

export const getPreviousTaskFlowInstance = (
  taskFlowDefinition: TTaskFlowDefinition,
  context: ITaskFlowContext,
) => {
  const user = selectCurrentUser(context.getState());
  if (!user) throw new Error('Something went wrong, user not found');
  const taskFlowStorageManager = TodayTaskStorage(taskFlowDefinition, user.id);

  const taskFlowInstance = taskFlowStorageManager.get();
  // TodayTaskStorage uses a shared key across all flows. Discard stale instances
  // from a different flow to prevent cross-flow session mapping issues.
  if (taskFlowInstance && taskFlowInstance.taskFlowDefinitionId !== taskFlowDefinition.id) {
    return null;
  }
  return taskFlowInstance;
};

const getTaskFromSession = (session: TSessionDefinition, taskFlowContext: ITaskFlowContext) => {
  if (session.taskSelectionOrder === 'randomized') {
    const notCompletedTasks = getNotCompletedTasksInSession(session, taskFlowContext);
    return sample(notCompletedTasks);
  } else {
    const notCompletedTasks = getNotCompletedTasksInSession(session, taskFlowContext);
    return notCompletedTasks && notCompletedTasks.length > 0 ? notCompletedTasks[0] : null;
  }
};

export const getTaskFlowInstance = (
  taskFlowDefinition: TTaskFlowDefinition,
  context: ITaskFlowContext,
) => {
  const user = selectCurrentUser(context.getState());
  if (!user) throw new Error('Something went wrong, user not found');
  const taskFlowStorageManager = TodayTaskStorage(taskFlowDefinition, user.id);
  let taskFlowInstance = taskFlowStorageManager.get();

  // TodayTaskStorage uses a shared key across all flows. Discard stale instances
  // from a different flow to prevent cross-flow session mapping issues.
  if (taskFlowInstance && taskFlowInstance.taskFlowDefinitionId !== taskFlowDefinition.id) {
    taskFlowInstance = null;
  }

  if (taskFlowInstance) {
    const nextTaskFlowInstance = getNextTaskFlowInstance(
      taskFlowDefinition,
      context,
      taskFlowInstance,
      false,
    );
    const result = fastForwardCompletedSessions(taskFlowDefinition, context, nextTaskFlowInstance);
    taskFlowStorageManager.set(result);

    return result;
  } else {
    const currentSession = taskFlowDefinition.sessions.find(
      (session) =>
        !session.isSessionCompleted({
          sessionSignaled: false,
        }),
    );
    if (!currentSession) throw new Error('Something went wrong, session not found');

    const notCompletedTasks = getNotCompletedTaskIdsInSession(currentSession, context);
    const availableTasks = getAvailableTaskIdsInSession(currentSession, context);

    const currentTask = getTaskFromSession(currentSession, context);
    // All tasks in the initial session may be auto-completed (e.g. market-gated).
    // Use the fallback task so fastForwardCompletedSessions can advance past it.
    const currentTaskId =
      currentTask?.id ?? currentSession.fallbackTask?.id ?? currentSession.tasks[0]?.id;
    if (!currentTaskId) throw new Error('Something went wrong, current task not found');

    taskFlowInstance = {
      currentSession: {
        id: currentSession.id,
        started: Date.now(),
        signaled: false,
        notCompletedTasks,
        availableTasks,
      },
      currentTaskId,
      taskFlowDefinitionId: taskFlowDefinition.id,
    };

    const result = fastForwardCompletedSessions(taskFlowDefinition, context, taskFlowInstance);
    taskFlowStorageManager.set(result);

    return result;
  }
};

export const saveTaskFlowInstance = (
  taskFlowDefinition: TTaskFlowDefinition,
  context: ITaskFlowContext,
  taskFlowInstance: TTaskFlowInstance,
) => {
  const user = selectCurrentUser(context.getState());
  if (!user) throw new Error('Something went wrong, user not found');

  const taskFlowStorageManager = TodayTaskStorage(taskFlowDefinition, user.id);
  taskFlowStorageManager.set(taskFlowInstance);
};

export const getCurrentSession = (
  taskFlowDefinition: TTaskFlowDefinition,
  context: ITaskFlowContext,
) => {
  const taskFlowInstance = getTaskFlowInstance(taskFlowDefinition, context);
  const currentSession = taskFlowDefinition.sessions.find(
    (session) => session.id === taskFlowInstance.currentSession.id,
  );
  if (!currentSession) throw new Error('Something went wrong');
  return currentSession;
};

const getNextSession = (
  taskFlowDefinition: TTaskFlowDefinition,
  taskFlowInstance: TTaskFlowInstance,
) => {
  let currentSession = taskFlowDefinition.sessions.find(
    (session) => session.id === taskFlowInstance.currentSession.id,
  );

  if (!currentSession)
    currentSession = taskFlowDefinition.sessions[taskFlowDefinition.sessions.length - 1];

  if (
    currentSession.isSessionCompleted({
      sessionSignaled: taskFlowInstance.currentSession.signaled,
      sessionStarted: taskFlowInstance.currentSession.started,
    })
  ) {
    const currentSessionIndex = taskFlowDefinition.sessions.findIndex(
      (session) => session.id === taskFlowInstance.currentSession.id,
    );

    const nextSessionIndex =
      taskFlowDefinition.sessions.length <= currentSessionIndex + 1
        ? currentSessionIndex
        : currentSessionIndex + 1;

    return taskFlowDefinition.sessions[nextSessionIndex];
  } else {
    return currentSession;
  }
};

export const signalTaskFlow = (
  taskFlowDefinition: TTaskFlowDefinition,
  context: ITaskFlowContext,
) => {
  const user = selectCurrentUser(context.getState());
  if (!user) throw new Error('Something went wrong, user not found');

  const taskFlowStorageManager = TodayTaskStorage(taskFlowDefinition, user.id);
  let storedInstance = taskFlowStorageManager.get();

  if (storedInstance && storedInstance.taskFlowDefinitionId !== taskFlowDefinition.id) {
    storedInstance = null;
  }

  if (!storedInstance) {
    // No valid stored instance — initialize fresh (includes fast-forward)
    return getTaskFlowInstance(taskFlowDefinition, context);
  }

  // Time-based advancement (without signal)
  const timeAdvanced = getNextTaskFlowInstance(taskFlowDefinition, context, storedInstance, false);

  // Apply signal to advance past the current session
  const signaled = getNextTaskFlowInstance(taskFlowDefinition, context, timeAdvanced, true);

  // Fast-forward past sessions where all tasks are already completed
  const result = fastForwardCompletedSessions(taskFlowDefinition, context, signaled);

  taskFlowStorageManager.set(result);
  return result;
};

export const migrateToNewTaskFlowDefinition = (
  taskFlowDefinition: TTaskFlowDefinition,
  previousTaskFlowInstance: TTaskFlowInstance,
  context: ITaskFlowContext,
) => {
  const user = selectCurrentUser(context.getState());

  if (!user) throw new Error('Something went wrong, user not found');

  const nextTaskFlowInstance = migrateTaskFlowInstance(
    taskFlowDefinition,
    context,
    previousTaskFlowInstance,
  );

  const result = fastForwardCompletedSessions(taskFlowDefinition, context, nextTaskFlowInstance);

  const taskFlowStorageManager = TodayTaskStorage(taskFlowDefinition, user.id);

  taskFlowStorageManager.set(result);
  return result;
};
